import _, {concat, isEqual, omit, union, uniq, without} from 'lodash';

import {arrayToggle} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {createObjectPropertyUpdater, createUpdater} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {AsyncBufferWaiter} from '../../../helpers/asyncHelpers';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {EmailFormatsEnum} from '../../../helpers/models/emailFormatHelpers';
import {findChildTagIdsToRemove, findParentTags} from '../../../helpers/models/tagHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {CustomSnoozeModel, SettingsCustomSnoozeModel} from '../../../models/api/customSnoozeModel';
import {ApiPreferencesDataGroup} from '../../../models/api/dataGroupModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {
  CountersVisibilityModesEnum,
  DismissedAlertsEnum,
  DoNotDisturb,
  OnboardingTipTypesEnum,
  parsePreferencesError,
  PreferencesModel,
  serializeBadgeNamespace,
  serializePreferences,
  serializeTeammatePreferences,
  SkinTonesEnum,
  StartGuideTaskTypesEnum,
  SwipeActionsEnum,
} from '../../../models/api/preferencesModel';
import {TagModel} from '../../../models/api/tagModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {postAsync, putApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {findPreferencesQueue} from '../../../services/tasks/specializedTaskQueues';
import {PreferencesError} from '../../../settings/preferences/preferencesHelpers';
import {SnoozesError} from '../../../settings/snoozes/settingsSnoozesHelpers';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {updateDataAction} from '../dataGroupsActions';
import {getOptimisticData} from '../dataSelectors';
import {DataState} from '../dataState';
import {getTags} from '../tags/tagsSelectors';
import {getUser} from '../teammates/teammatesSelectors';
import {
  getCustomSnoozesCombined,
  getDisplayedTeammateIds,
  getPreferences,
  getVisibleCustomViewIds,
} from './preferencesSelectors';

/*
 * Action types.
 */

export enum PreferencesActionsEnum {
  PREFERENCES_UPDATED = '@@data/userPreferences/PREFERENCES_UPDATED',
}

/*
 * Types.
 */

export interface PreferencesUpdatedAction extends ActionWithPayload<PreferencesModel> {
  type: PreferencesActionsEnum.PREFERENCES_UPDATED;
}

/*
 * Action creators.
 */

export function hideInboxAction(inboxId: number): ThunkAction {
  return (dispatch, getState) => {
    const {visibleInboxIds} = getState().data.preferences;
    const newVisibleInboxIds = arrayToggle(visibleInboxIds, inboxId);

    return dispatch(savePreferencesAction({visibleInboxIds: newVisibleInboxIds}));
  };
}

export function hideTeammateAction(teammateId: number): ThunkAction {
  return (dispatch, getState) => {
    const displayedTeammateIds = getDisplayedTeammateIds(getState());
    const newDisplayedTeammateIds = arrayToggle(displayedTeammateIds, teammateId);

    return dispatch(savePreferencesAction({displayedTeammateIds: newDisplayedTeammateIds}));
  };
}

export function updateWorkspacePreferencesAction({
  teammateIds,
  tagAndViewIds,
  inboxIds,
  shouldAlphabetizeWorkspace,
}: {
  teammateIds: ReadonlyArray<TeammateModel['id']>;
  tagAndViewIds: ReadonlyArray<TagModel['id']>;
  inboxIds: ReadonlyArray<InboxModel['id']>;
  shouldAlphabetizeWorkspace: boolean;
}): ThunkAction {
  return savePreferencesAction({
    displayedTeammateIds: teammateIds,
    visibleCustomViewIds: tagAndViewIds,
    visibleInboxIds: inboxIds,
    shouldAlphabetizeWorkspace,
  });
}

export function updateShouldAlphabetizeWorkspaceAction(newValue: boolean) {
  return maybeUpdatePreferenceAction('shouldAlphabetizeWorkspace', newValue);
}

export function updateLastAutoRecommendationAction(newValue: number): ThunkAsyncAction {
  return async (dispatch) => {
    await dispatch(maybeUpdatePreferenceAction('lastAutoRecommendation', newValue));
  };
}

export function updateLastEditModeHelpTipAction(newValue: number): ThunkAsyncAction {
  return async (dispatch) => {
    await dispatch(maybeUpdatePreferenceAction('lastEditModeHelpTip', newValue));
  };
}

export function toggleHideFrameAction(frameId: string): ThunkAction {
  return (dispatch, getState) => {
    const {hiddenFrameIds} = getState().data.preferences;
    const newHiddenFrameIds = arrayToggle(hiddenFrameIds, frameId);
    const preferencesToUpdate = {hiddenFrameIds: newHiddenFrameIds};
    const {sortedFrameIds} = getState().data.preferences;
    // TODO: Once the migration is done and we remove hiddenFrameIds, toggle sortedFrameIds.
    const newSortedFrameIds = hiddenFrameIds.includes(frameId)
      ? [...new Set([...sortedFrameIds, frameId])]
      : without(sortedFrameIds, frameId);
    Object.assign(preferencesToUpdate, {sortedFrameIds: newSortedFrameIds});

    return dispatch(savePreferencesAction(preferencesToUpdate));
  };
}

export function pinFrameAction(frameId: string): ThunkAction {
  return (dispatch, getState) => {
    const {sortedFrameIds} = getState().data.preferences;
    if (!sortedFrameIds.includes(frameId)) {
      return dispatch(savePreferencesAction({sortedFrameIds: [...new Set([...sortedFrameIds, frameId])]}));
    }
    return null;
  };
}

export function toggleListAutoFormattingAction(): ThunkAction {
  return (dispatch, getState) => {
    const {listAutoFormatting} = getState().data.preferences;

    return dispatch(savePreferencesAction({listAutoFormatting: !listAutoFormatting}));
  };
}

interface ToggleHideTagOptions {
  saveGate?: AsyncBufferWaiter;
  /** Explicit value to change the item's visibility to (true means show, false means hide). If not provided, the visibility will be toggled. */
  value?: boolean;
}

export function toggleHideTagAction(
  tagId: number,
  tags: ReadonlyArray<TagModel>,
  options: ToggleHideTagOptions = {},
): ThunkAction {
  return (dispatch, getState) => {
    const {saveGate} = options;
    const state = getState();
    const visibleCustomViewIds = getVisibleCustomViewIds(state);

    const isTagBeingAdded = options.value ?? !visibleCustomViewIds.includes(tagId);
    // Combine the tags passed with those in the state, as they may not be there yet.
    const combinedTags = _(tags)
      .filter((t) => !getTags(state)[t.id])
      .keyBy((t) => t.id)
      .assign(getTags(state))
      .value();

    const newVisibleCustomViewIds = (() => {
      if (isTagBeingAdded) {
        // We may need to add the parents of the tag to P1
        const parentTagIdsToAdd = findParentTags(combinedTags, tags[0] || combinedTags[tagId])
          .map((t) => t.id)
          .filter((tId) => !visibleCustomViewIds.includes(tId));

        return union(visibleCustomViewIds, [tagId, ...parentTagIdsToAdd]);
      }

      const idsToRemove = findChildTagIdsToRemove({tagId, visibleTagIds: visibleCustomViewIds, combinedTags});

      return without(visibleCustomViewIds, ...idsToRemove);
    })();

    // If there's no change, no need to dispatch
    if (visibleCustomViewIds.length === newVisibleCustomViewIds.length) {
      return null;
    }

    const saveOptions = {saveGate, tags};
    return dispatch(savePreferencesAction({visibleCustomViewIds: newVisibleCustomViewIds}, saveOptions));
  };
}

export function updateShouldShowNewTagInWorkspacesAction(newValue: boolean) {
  return maybeUpdatePreferenceAction('shouldShowNewTagInWorkspaces', newValue);
}

export function updateLastEmailFormatAction(newValue: EmailFormatsEnum): ThunkAction {
  return maybeUpdatePreferenceAction('lastEmailFormat', newValue);
}

export function updateEmojiSkinToneAction(skinTone: SkinTonesEnum): ThunkAction {
  return savePreferencesAction({emojiSkinTone: skinTone});
}

export function updateShouldSendOnEnterForChatAction(newValue: boolean): ThunkAction {
  return maybeUpdatePreferenceAction('shouldSendOnEnterForChat', newValue);
}

export function updateShouldMuteNotificationsAction(newValue: boolean): ThunkAction {
  return maybeUpdatePreferenceAction('shouldMuteNotifications', newValue);
}

export function updateCountersVisibilityModeAction(
  namespace: string,
  newValue: CountersVisibilityModesEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const {countersVisibilities} = getState().data.preferences;
    const existingValue = countersVisibilities[namespace];

    if (existingValue && existingValue === newValue) {
      return null;
    }

    return dispatch(
      savePreferencesAction({
        countersVisibilities: {
          ...countersVisibilities,
          [namespace]: newValue,
        },
      }),
    );
  };
}

export function updateDoNotDisturbAction(newValue: DoNotDisturb): ThunkAction {
  return maybeUpdatePreferenceAction('doNotDisturb', newValue, isEqual);
}

export function updateDefaultFontStyleAction(newValue: string): ThunkAction {
  return maybeUpdatePreferenceAction('defaultFontStyle', newValue);
}

export function updateSeenOnboardingTipsAction(
  onboardingTips: ReadonlyArray<OnboardingTipTypesEnum>,
  shouldMarkUnseen?: boolean,
): ThunkAction {
  return (dispatch, getState) => {
    const {seenOnboardingTips} = getState().data.preferences;

    const hasSeenInfoBubbles = onboardingTips.every((onboardingTip) =>
      seenOnboardingTips.includes(onboardingTip),
    );

    // If we want to mark this bubble as unseen, and it's in the list, remove it.
    if (shouldMarkUnseen && hasSeenInfoBubbles) {
      return dispatch(
        savePreferencesAction({seenOnboardingTips: without(seenOnboardingTips, ...onboardingTips)}),
      );
    }

    // If we want to mark this bubble as seen, and it's not in the list, add it.
    if (!shouldMarkUnseen && !hasSeenInfoBubbles) {
      return dispatch(
        savePreferencesAction({seenOnboardingTips: uniq([...seenOnboardingTips, ...onboardingTips])}),
      );
    }

    return null;
  };
}

export function updateSortedFrameIdsAction(newFrameIds: ReadonlyArray<string>) {
  return savePreferencesAction({sortedFrameIds: newFrameIds});
}

export function updateShouldUseOpenFilterForTeamsAction(shouldUseOpenFilterForTeams: boolean) {
  return savePreferencesAction({shouldUseOpenFilterForTeams});
}

export function resubscribeAllVisitorNotificationsAction() {
  return maybeUpdatePreferenceAction('isVisitorSubscribedAll', true);
}

export function updateShouldShowNewTagInCellsAction(newValue: boolean) {
  return maybeUpdatePreferenceAction('shouldShowNewTagInCells', newValue);
}

export function updateHasSeenTeamTabsAlertAction(newValue: boolean) {
  return maybeUpdatePreferenceAction('hasSeenTeamTabsAlert', newValue);
}

export function updateHasSeenDomainLoginBannerAction() {
  return maybeUpdatePreferenceAction('hasSeenDomainLoginBanner', true);
}

export function updateShouldCalendarDisplayWeekendsAction(newValue: boolean) {
  return maybeUpdatePreferenceAction('shouldCalendarDisplayWeekends', newValue);
}

export function updateIgnoreQuickRuleTagIdsAction(newTagIds: ReadonlyArray<number>): ThunkAction {
  return (dispatch, getState) => {
    const {ignoreQuickRuleTagIds} = getState().data.preferences;
    const newIgnoreQuickRuleTagIds = ignoreQuickRuleTagIds.slice(-99);

    return dispatch(
      maybeUpdatePreferenceAction('ignoreQuickRuleTagIds', concat(newIgnoreQuickRuleTagIds, newTagIds)),
    );
  };
}

export function updateStartGuideTasksAction(startGuideTask: StartGuideTaskTypesEnum): ThunkAction {
  return (dispatch, getState) => {
    const {completedStartGuideTasks} = getState().data.preferences;

    const hasSeenStartGuideTask = completedStartGuideTasks.includes(startGuideTask);

    if (!hasSeenStartGuideTask) {
      return dispatch(
        savePreferencesAction({completedStartGuideTasks: [...completedStartGuideTasks, startGuideTask]}),
      );
    }

    return null;
  };
}

export function updateDismissedAlertsAction(alertToDismiss: DismissedAlertsEnum): ThunkAction {
  return (dispatch, getState) => {
    const {dismissedAlerts} = getState().data.preferences;

    const newDismissedAlerts = arrayToggle(dismissedAlerts, alertToDismiss);

    return dispatch(
      savePreferencesAction({
        dismissedAlerts: newDismissedAlerts,
      }),
    );
  };
}

export function addRecentAnswerAction(answerId: number): ThunkAction {
  return (dispatch, getState) => {
    const {recentAnswerIds} = getState().data.preferences;

    const newRecentAnswerIds = _([...recentAnswerIds])
      // Add the last used answer id to the front of the array.
      .unshift(answerId)
      // Remove duplicate occurances of that id.
      .uniq()
      // Keep the first 4.
      .take(4)
      .value();

    return dispatch(
      savePreferencesAction({
        recentAnswerIds: newRecentAnswerIds,
      }),
    );
  };
}

export function updateCalendarTimezoneAction(newValue: string): ThunkAsyncAction {
  return async (dispatch) => {
    await dispatch(maybeUpdatePreferenceAction('calendarTimezone', newValue));
  };
}

export function updateHasDismissedAccountsSetupGuideAction(newValue: boolean): ThunkAction {
  return maybeUpdatePreferenceAction('hasDismissedAccountsSetupGuide', newValue);
}

/**
 * Add or remove a pin from the list of pinned analytics views.
 */
export function setAnalyticsViewPinnedAction(
  viewId: number,
  shouldPin: boolean,
): ThunkAsyncAction<PreferencesModel | null> {
  return (dispatch, getState) => {
    const {analyticsPinnedViews} = getState().data.preferences;

    if (shouldPin) {
      return dispatch(
        maybeUpdatePreferenceAction('analyticsPinnedViews', uniq([...analyticsPinnedViews, viewId]), isEqual),
      );
    }

    return dispatch(
      maybeUpdatePreferenceAction('analyticsPinnedViews', without(analyticsPinnedViews, viewId), isEqual),
    );
  };
}

/**
 * Update the map of namespace to default analytics views. This function uses the visibility
 * namespace of the view, not the namespace the view lives in, since there is a 1:1 relationship
 * from visiblity namespace to defualts.
 * Passing `undefined` as viewId will remove the entry from the map
 */
export function updateAnalyticsDefaultViewsAction(
  visibilityNamespace: string,
  viewId: number | undefined,
): ThunkAsyncAction<PreferencesModel | null> {
  return (dispatch, getState) => {
    const {analyticsDefaultViews} = getState().data.preferences;

    if (viewId === undefined) {
      return dispatch(
        maybeUpdatePreferenceAction(
          'analyticsDefaultViews',
          omit(analyticsDefaultViews, visibilityNamespace),
          isEqual,
        ),
      );
    }

    return dispatch(
      maybeUpdatePreferenceAction(
        'analyticsDefaultViews',
        {...analyticsDefaultViews, [visibilityNamespace]: viewId},
        isEqual,
      ),
    );
  };
}

export function updateDateTimeFormat(newValue: TimeFormatsEnum): ThunkAction {
  return maybeUpdatePreferenceAction('timeFormat', newValue);
}

// TODO: Make this pattern reusable, and patch preferences more granularly everywhere. (Patching _all_ preferences
// every time may cause race conditions.)
export function updateBadgeNamespaceAsyncAction(newBadgeNamespace: string | undefined): ThunkAsyncAction {
  const optimisticUpdater = createObjectPropertyUpdater<DataState, 'preferences'>(
    'preferences',
    createUpdater((preferences) => ({
      ...preferences,
      badgeNamespace: newBadgeNamespace,
    })),
  );

  return async (dispatch, getState) =>
    dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () => {
        try {
          const user = getUser(getState());
          const dataGroup = await putApiAsync(ApiPreferencesDataGroup, user.url, {
            preferences: {
              badgeNotifications: serializeBadgeNamespace(newBadgeNamespace),
            },
          });

          return updateDataAction(dataGroup);
        } catch (error) {
          if (!(error instanceof HttpError)) {
            reportToBugsnag(error);
          }

          throw new PreferencesError(parsePreferencesError(error));
        }
      }),
    );
}

export function updateLeftToRightSwipeAction(newValue: SwipeActionsEnum): ThunkAction {
  return maybeUpdatePreferenceAction('leftToRightSwipeAction', newValue);
}

export function updateRightToLeftSwipeAction(newValue: SwipeActionsEnum): ThunkAction {
  return maybeUpdatePreferenceAction('rightToLeftSwipeAction', newValue);
}

export function updateQuickReplyEnabled(newValue: boolean): ThunkAction {
  return maybeUpdatePreferenceAction('quickReplyEnabled', newValue);
}

export function updateBackButtonOpensP1(newValue: boolean): ThunkAction {
  return maybeUpdatePreferenceAction('backButtonOpensP1', newValue);
}

/*
 * Base actions.
 */

type PreferencesKey = keyof PreferencesModel;
type PreferencesValue<K extends PreferencesKey> = PreferencesModel[K];
type PreferencesEqualityCheck<K extends PreferencesKey> = (
  a: PreferencesValue<K>,
  b: PreferencesValue<K>,
) => boolean;

const isStrictEqual = (a: any, b: any) => a === b;

/** Dispatches a save preferences task if and only if the given preference value changed. */
function maybeUpdatePreferenceAction<K extends PreferencesKey>(
  key: K,
  newValue: PreferencesValue<K>,
  equalityCheck: PreferencesEqualityCheck<K> = isStrictEqual,
): ThunkAsyncAction<PreferencesModel | null> {
  return async (dispatch, getState) => {
    const {preferences} = getState().data;
    const existingValue = preferences[key];

    if (equalityCheck(existingValue, newValue)) {
      return null;
    }

    return dispatch(
      savePreferencesAction({
        [key]: newValue,
      }),
    );
  };
}
interface SavePreferencesOptions {
  saveGate?: AsyncBufferWaiter;
  tags?: ReadonlyArray<TagModel>;
}

export function saveTeammatePreferencesAction(
  preferencesUpdate: Partial<PreferencesModel>,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'preferences'>(
      'preferences',
      createUpdater((preferences) => ({
        ...preferences,
        ...preferencesUpdate,
        updatedAt: preferences.updatedAt,
      })),
    );

    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () => {
        try {
          const {preferences} = getOptimisticData(getState());
          // Perform the request.
          const user = getUser(getState());
          const body = serializeTeammatePreferences({...preferences, ...preferencesUpdate});
          const serverDataGroup = await putApiAsync(ApiPreferencesDataGroup, user.url, body);

          return updateDataAction({preferences: serverDataGroup.preferences});
        } catch (error) {
          if (!(error instanceof HttpError)) {
            reportToBugsnag(error);
          }

          throw new PreferencesError(parsePreferencesError(error));
        }
      }),
    );
  };
}

export function savePreferencesAction(
  preferencesUpdate: Partial<PreferencesModel>,
  options: SavePreferencesOptions = {},
): ThunkAsyncAction<PreferencesModel | null> {
  return async (dispatch, getState) => {
    // Find the existing preferences.
    const queue = findPreferencesQueue(dispatch, getState);
    const preferencesInitial = queue.get();
    if (!preferencesInitial) {
      throw new Error('Preferences should already exist.');
    }

    // Update them immediately.
    const newPreferences = {
      ...preferencesInitial,
      ...preferencesUpdate,
      updatedAt: preferencesInitial.updatedAt,
    };

    const {saveGate, tags} = options;
    queue.update({preferences: newPreferences, tags});

    await queue.waitForTurnAsync();

    const preferencesAfterWait = queue.getResult();
    if (!preferencesAfterWait) {
      throw new Error('Preferences should already exist.');
    }

    // If a save gate was provided, check that we're allowed to save.
    if (saveGate && !(await saveGate).isLastWaiter) {
      return queue.provideResultAsync({});
    }

    try {
      // Perform the request.
      const user = getUser(getState());
      const body = serializePreferences({...preferencesAfterWait, ...preferencesUpdate});
      const serverDataGroup = await putApiAsync(ApiPreferencesDataGroup, user.url, body);

      return queue.provideResultAsync(serverDataGroup);
    } catch (error) {
      queue.reportError();
      throw error;
    }
  };
}

export function saveWasOnboardingCompletedAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const user = getUser(state);
    const preferences = getPreferences(state);

    // We need to post to a special endpoint instead of simply updating the preference.
    // This endpoint also handles trial end.
    await postAsync(`${user.url}/complete-onboarding`);

    // Don't use savePreferencesAction because we want to update Redux only.
    // /complete-onboarding will take care of updating the server.
    dispatch({
      payload: {
        ...preferences,
        wasOnboardingCompleted: true,
      },
      type: PreferencesActionsEnum.PREFERENCES_UPDATED,
    });
  };
}

export function saveCustomSnoozeAction(
  snooze: SettingsCustomSnoozeModel,
): ThunkAsyncAction<PreferencesModel | null> {
  return async (dispatch, getState) => {
    const existingSnoozes = getCustomSnoozesCombined(getState()) || [];
    const existingSnooze = existingSnoozes[snooze.id - 1];

    if (!existingSnooze) {
      throw new SnoozesError(`No snooze was found with id ${snooze.id}`);
    }

    const newSnoozes = [...existingSnoozes];
    newSnoozes[snooze.id - 1] = snooze;

    return dispatch(
      savePreferencesAction({
        customSnoozes: newSnoozes,
      }),
    );
  };
}

export function createCustomSnoozeAction(
  snooze: CustomSnoozeModel,
): ThunkAsyncAction<PreferencesModel | null> {
  return async (dispatch, getState) => {
    const existingSnoozes = getCustomSnoozesCombined(getState());

    // Otherwise, simply add our newly created snooze
    return dispatch(
      savePreferencesAction({
        customSnoozes: [...existingSnoozes, snooze],
      }),
    );
  };
}

export function deleteBulkCustomSnoozeAction(
  snoozeIds: ReadonlyArray<number>,
): ThunkAsyncAction<PreferencesModel | null> {
  return async (dispatch, getState) => {
    const existingSnoozes = getCustomSnoozesCombined(getState()) || [];
    const snoozesToDelete = _(snoozeIds)
      .map((id) => existingSnoozes[id - 1])
      .compact()
      .value();

    const newSnoozes = _.without([...existingSnoozes], ...snoozesToDelete);

    return dispatch(
      savePreferencesAction({
        customSnoozes: newSnoozes,
      }),
    );
  };
}

export function deleteCustomSnoozeAction(
  snooze: SettingsCustomSnoozeModel,
): ThunkAsyncAction<PreferencesModel | null> {
  return async (dispatch, getState) => {
    const existingSnoozes = getCustomSnoozesCombined(getState()) || [];
    const existingSnooze = existingSnoozes[snooze.id - 1];

    if (!existingSnooze) {
      throw new SnoozesError(`No snooze was found with id ${snooze.id}`);
    }

    const newSnoozes = _.without([...existingSnoozes], existingSnooze);

    return dispatch(
      savePreferencesAction({
        customSnoozes: newSnoozes,
      }),
    );
  };
}

/*
 * Union type.
 */

export type PreferencesAction = PreferencesUpdatedAction;
